import create, { GetState, SetState } from "zustand";

import { IAuthInfo } from "@shared-types/auth";
import { SchoolInfo } from "@shared-types/school";
import { SocialInfoInterface } from "@shared-types/social";

// import { ROLES } from "@constant";
import { ROLES } from "@/constants/auth.constant";
import { KycDetails, PAYMENT_MODE } from "@shared-types/kyc";
import { NAVBAR_PRIMARY_CTA_TYPE } from "@shared-types/navbar";
// TODO: Move Kyc related actions and states to useInstructorStore

// New Global Store
export type GlobalStore = {
  schoolInfo?: SchoolInfo;
  authInfo?: IAuthInfo;
  socialInfo: SocialInfoInterface[];
  isGlobalLoading: boolean;
  isSubscribed: boolean;
  isInstructor: boolean;
  authenticated: boolean;
  role: any;
  s_uid: any;
  u_uid: any;
  scenesKeyAvailable?: string;
  kycDetails?: KycDetails;
  isKycFormOpened: boolean;
  isKycDetailsOpened: boolean;
  kycStatusAfterFormSubmit: string;
  showPaymentActivationModal: boolean;
  paymentActivationSource: string;
  stripeActivationLink: string;
  showLoginModal?: boolean;
  showAuthActionOnLandingPage: boolean;
  toggleShowPaymentActivationModal: (_: boolean) => void;
  setPaymentActivationSource: (_: string) => void;
  setStripeActivationLink: (_: string) => void;
  isMemberInstructor: boolean | null;
  hasMultipleInstructors?: boolean;
  isDashboardEmpty: boolean | null;
  schoolPaymentMode: PAYMENT_MODE;
  // Keeping it any since it's dependent on third party library. If it changes it will break
  ipInfo: any;
  timezone: any;
  showScheduleDemoModal: boolean;
  showScheduleDemoSuccessModal: boolean;
  openScheduleFormOnScheduleCTAClick: boolean;
  navbarPrimaryCtaType: NAVBAR_PRIMARY_CTA_TYPE;
  scheduleDemoSuccessEmail?: string;
  scheduleDemoSuccessName?: string;

  setScenesKeyAvailable: (scenesKeyAvailable: string) => void;
  setSocialInfo: (socialInfo: any) => void;
  setSchoolInfo: (schoolInfo: any, socialInfo: any) => void;
  setAuthInfo: (schoolInfo: any, role: any) => void;
  setGlobalLoading: (isGlobalLoading: any) => void;
  setKycDetails: (kycDetails: KycDetails) => void;
  setIsKycFormOpened: (isKycFormOpened: boolean) => void;
  setIsKycDetailsOpened: (isKycDetailsOpened: boolean) => void;
  setKycStatusAfterFormSubmit: (kycStatusAfterFormSubmit: string) => void;
  setIsPaymentActive: (isPaymentActive: boolean) => void;
  updateSchoolInfoData: (key: string, value: string) => void;
  updateAuthInfoData: (key: string, value: string) => void;
  setShowLoginModal: (showLoginModal?: boolean) => void;
  setMemberInstructor: (
    isMemberInstructor: boolean | null,
    hasMultipleInstructors?: boolean
  ) => void;
  setIsDashboardEmpty: (isDashboardEmpty: boolean | null) => void;
  setIpInfo: (countryCode: any, timezone: any) => void;
  setSchoolPaymentMode: (mode: PAYMENT_MODE) => void;
  setShowAuthActionOnLandingPage: (show: boolean) => void;
  setShowScheduleDemoModal: (show: boolean) => void;
  setShowScheduleDemoSuccessModal: (show: boolean) => void;
  setNavbarPrimaryCtaType: (type: NAVBAR_PRIMARY_CTA_TYPE) => void;
  setOpenScheduleFormOnScheduleCTAClick: (open: boolean) => void;
  setScheduleDemoSuccessEmail: (email: string) => void;
  setScheduleDemoSuccessName: (name: string) => void;
};

export const useGlobalStore = create<GlobalStore>(
  (set: SetState<GlobalStore>, get: GetState<GlobalStore>) => ({
    schoolInfo: undefined,
    authInfo: undefined,
    socialInfo: [],
    isGlobalLoading: true,
    authenticated: false,
    isSubscribed: false,
    isInstructor: false,
    role: null,
    s_uid: null,
    u_uid: null,
    kycDetails: undefined,
    isKycFormOpened: false,
    isKycDetailsOpened: false,
    kycStatusAfterFormSubmit: "",
    showPaymentActivationModal: false,
    paymentActivationSource: "",
    stripeActivationLink: "",
    showLoginModal: false,
    isMemberInstructor: null,
    hasMultipleInstructors: false,
    isDashboardEmpty: null,
    ipInfo: null,
    scenesKeyAvailable: "",
    timezone: null,
    schoolPaymentMode: PAYMENT_MODE.EXTERNAL,
    showAuthActionOnLandingPage: true,
    showScheduleDemoModal: false,
    showScheduleDemoSuccessModal: false,
    openScheduleFormOnScheduleCTAClick: false,
    navbarPrimaryCtaType: NAVBAR_PRIMARY_CTA_TYPE.SIGNUP,
    setScenesKeyAvailable: (scenesKeyAvailable: string): void =>
      set((state) => {
        state.scenesKeyAvailable = scenesKeyAvailable;
      }),
    setSocialInfo: (socialInfo: any): void =>
      set((state) => {
        state.socialInfo = socialInfo;
      }),
    setGlobalLoading: (isGlobalLoading: boolean): void =>
      set((state) => {
        state.isGlobalLoading = isGlobalLoading;
      }),
    setSchoolInfo: (
      schoolInfo: SchoolInfo,
      socialInfo: SocialInfoInterface[]
    ): void =>
      set((state) => {
        state.schoolInfo = schoolInfo;
        state.socialInfo = socialInfo;
        state.s_uid = schoolInfo?.uuid;
        state.isGlobalLoading = false;
      }),
    setKycDetails: (kycDetails: KycDetails): void =>
      set((state) => {
        state.kycDetails = kycDetails;
      }),
    setIsKycFormOpened: (isKycFormOpened: boolean): void =>
      set((state) => {
        state.isKycFormOpened = isKycFormOpened;
      }),
    setIsKycDetailsOpened: (isKycDetailsOpened: boolean): void =>
      set((state) => {
        state.isKycDetailsOpened = isKycDetailsOpened;
      }),
    setKycStatusAfterFormSubmit: (kycStatusAfterFormSubmit: string): void =>
      set((state) => {
        state.kycStatusAfterFormSubmit = kycStatusAfterFormSubmit;
      }),
    setAuthInfo: (authInfo: IAuthInfo, role: any): void =>
      set((state) => {
        state.authInfo = authInfo;
        state.authenticated = true;
        state.role = role;
        state.isInstructor = role === ROLES.INSTRUCTOR.toLowerCase();
        state.isSubscribed = role === ROLES.LEARNER.toLowerCase();
        state.u_uid = authInfo?.uuid;
        state.isGlobalLoading = false;
      }),
    toggleShowPaymentActivationModal: (
      showPaymentActivationModal: boolean
    ): void =>
      set((state) => {
        state.showPaymentActivationModal = showPaymentActivationModal;
      }),
    setPaymentActivationSource: (paymentActivationSource: string): void =>
      set((state) => {
        state.paymentActivationSource = paymentActivationSource;
      }),
    setStripeActivationLink: (stripeActivationLink: string): void =>
      set((state) => {
        state.stripeActivationLink = stripeActivationLink;
      }),
    logout: (): void =>
      set((state) => {
        state.authInfo = undefined;
        state.authenticated = false;
        state.role = null;
        state.isInstructor = false;
        state.isSubscribed = false;
        state.u_uid = null;
        state.isGlobalLoading = false;
      }),
    setIpInfo: (countryCode: any, timezone: any) =>
      set((state) => {
        state.ipInfo = countryCode;
        state.timezone = timezone;
      }),
    setMemberInstructor: (
      isMemberInstructor: boolean | null,
      hasMultipleInstructors?: boolean
    ): void =>
      set((state) => {
        state.isMemberInstructor = isMemberInstructor;
        state.hasMultipleInstructors = hasMultipleInstructors;
      }),
    setIsPaymentActive: (isPaymentActive: boolean): void => {
      isPaymentActive;
      const schoolInfo: any = get().schoolInfo;
      return set((state) => {
        state.schoolInfo = {
          ...schoolInfo,
          is_payment_enabled: isPaymentActive,
        };
      });
    },
    updateSchoolInfoData: (key: string, value: string): void => {
      let schoolInfo: any = get().schoolInfo;

      schoolInfo = {
        ...schoolInfo,
        [key]: value,
      };

      set((state) => {
        state.schoolInfo = { ...schoolInfo };
      });
    },
    updateAuthInfoData: (key: string, value: string): void => {
      let authInfo: any = get().authInfo;

      authInfo = {
        ...authInfo,
        [key]: value,
      };

      set((state) => {
        state.authInfo = { ...authInfo };
      });
    },
    setShowLoginModal: (showLoginModal?: boolean): void =>
      set((state) => {
        state.showLoginModal = showLoginModal;
      }),
    setIsDashboardEmpty: (isDashboardEmpty: boolean | null): void =>
      set((state) => {
        state.isDashboardEmpty = isDashboardEmpty;
      }),
    setSchoolPaymentMode: (mode: PAYMENT_MODE): void => {
      set((state) => {
        state.schoolPaymentMode = mode;
      });
    },
    setShowAuthActionOnLandingPage: (show: boolean) => {
      set((state) => {
        state.showAuthActionOnLandingPage = show;
      });
    },
    setShowScheduleDemoModal: (show: boolean) => {
      set((state) => {
        state.showScheduleDemoModal = show;
      });
    },
    setShowScheduleDemoSuccessModal: (show: boolean) => {
      set((state) => {
        state.showScheduleDemoSuccessModal = show;
      });
    },
    setNavbarPrimaryCtaType: (type: NAVBAR_PRIMARY_CTA_TYPE) => {
      set((state) => {
        state.navbarPrimaryCtaType = type;
      });
    },
    setOpenScheduleFormOnScheduleCTAClick: (open: boolean) => {
      set((state) => {
        state.openScheduleFormOnScheduleCTAClick = open;
      });
    },
    setScheduleDemoSuccessEmail: (email: string) => {
      set((state) => {
        state.scheduleDemoSuccessEmail = email;
      });
    },
    setScheduleDemoSuccessName: (name: string) => {
      set((state) => {
        state.scheduleDemoSuccessName = name;
      });
    },
  })
);

export default useGlobalStore;
